import React, {useMemo, useState} from 'react'
import PropTypes from 'prop-types'
import {makeStyles} from '@mui/styles'
import moment from 'moment'
import {Spacing, Colors, Typography} from '../../assets/styles'
import {borderRadiusMedium} from '../../assets/styles/border'
import {getNPSBucket, NPS_BUCKET_PROMOTERS, NPS_BUCKET_PASSIVES, NPS_BUCKET_DETRACTORS} from '../../lib/MetricData/metricDataUtilities'
import {getMonthSelectorOptions} from '../ResidentSentiment/residentSentimentUtilities'
import SelectInput from '../SelectInput'
import ClearableInput from '../ClearableInput'
import DataComingSoon from '../DataComingSoon'
import EmptyStateIconAndMessage from '../EmptyStateIconAndMessage'
import RXRIcon from '../RXRIcon'
import ExitSurveyVideoPlayerModal from './ExitSurveyVideoPlayerModal'
import {formatResidentLine} from './exitSurveyVideoData'

const TITLE = 'Submission details'
const DATE_FORMAT = 'M/D/YYYY'
// the two dropdown filters share the same "no filter" sentinel value; the filters themselves are independent
const ALL_OPTION_VALUE = '__all__'
const ALL_CATEGORIES = ALL_OPTION_VALUE
const ALL_CATEGORIES_LABEL = 'Reason for leaving'
const REASONS_EYEBROW = 'Reasons for leaving'
const FEEDBACK_EYEBROW = 'Resident feedback'
const VIDEO_LINK_COPY = 'View video response'
const NO_MATCHES = 'No submissions match these filters.'
const RATING_DENOMINATOR = '/10'
const EMPTY_VALUE = 'N/A'
const LIST_MAX_HEIGHT = 856
const RATING_SIZE = 50

// per design, the "NPS:" prefix only shows on the closed control (via renderValue) — the open menu lists bare labels.
// Submissions without a numeric rating (a null bucket) only show under All Scores.
const NPS_SCORE_ALL = ALL_OPTION_VALUE
const npsScoreOptions = [
  {value: NPS_SCORE_ALL, label: 'All Scores'},
  {value: NPS_BUCKET_PROMOTERS, label: 'Promoters'},
  {value: NPS_BUCKET_PASSIVES, label: 'Passives'},
  {value: NPS_BUCKET_DETRACTORS, label: 'Detractors'},
]

const renderNpsScoreValue = option => `NPS: ${option.label}`

// the same rolling 30-day windows the NPS Details selector offers: 26 of them, each stepped back a week,
// keyed by the window's END date
const DATE_OPTIONS = getMonthSelectorOptions()
const DATE_WINDOW_DAYS = 30

/**
 * The categories actually present in the data, so the filter can never offer an empty result.
 * @param {Array<import('../../lib/MetricData/metricDataUtilities').ExitSurveySubmission>} submissions
 * @return {Array<{label: string, value: string}>}
 */
function categoryOptionsFrom(submissions) {
  const byId = new Map()
  submissions.forEach(submission =>
    (submission.reasonGroups || []).forEach(group => {
      if (!byId.has(group.id)) {
        byId.set(group.id, group.label)
      }
    }),
  )

  return [{label: ALL_CATEGORIES_LABEL, value: ALL_CATEGORIES}, ...[...byId.entries()].map(([value, label]) => ({label, value}))]
}

/**
 * @param {import('../../lib/MetricData/metricDataUtilities').ExitSurveySubmission} submission
 * @param {string} term already trimmed and lower-cased
 * @return {boolean}
 */
function matchesSearch(submission, term) {
  if (!term) {
    return true
  }

  const reasonGroups = submission.reasonGroups || []
  const haystack = [submission.residentName, submission.unitNumber]
    .concat(reasonGroups.map(group => group.label))
    .concat(reasonGroups.flatMap(group => (group.subReasons || []).map(sub => sub.label)))
    .filter(Boolean)
    .join(' ')
    .toLowerCase()

  return haystack.includes(term)
}

/**
 * One resident's submission: their rating, who they are, why they are leaving, and their video if they left one.
 * @param {Object} props
 * @param {import('../../lib/MetricData/metricDataUtilities').ExitSurveySubmission} props.submission
 * @param {function} props.onPlay
 */
function SubmissionRow({submission, onPlay}) {
  const classes = useStyles()
  const reasonGroups = submission.reasonGroups || []

  return (
    <div className={classes.row}>
      <div className={classes.rating}>
        <div className={classes.ratingValue}>{typeof submission.npsRating === 'number' ? submission.npsRating : EMPTY_VALUE}</div>
        <div className={classes.ratingDenominator}>{RATING_DENOMINATOR}</div>
      </div>

      <div className={classes.rowBody}>
        <div className={classes.rowHeader}>
          <div className={classes.residentName}>{formatResidentLine(submission)}</div>
          <div className={classes.submittedAt}>{submission.submittedAt ? moment(submission.submittedAt).format(DATE_FORMAT) : ''}</div>
        </div>

        {reasonGroups.length > 0 ? (
          <React.Fragment>
            <div className={classes.eyebrow}>{REASONS_EYEBROW}</div>
            {reasonGroups.map(group => (
              <div key={group.id} className={classes.reasonGroup}>
                <span className={classes.macroChip}>{group.label}</span>
                {(group.subReasons || []).length > 0 ? (
                  <div className={classes.subReasons}>
                    {group.subReasons.map(sub => (
                      <span key={sub.id} className={classes.subChip}>
                        {sub.label}
                      </span>
                    ))}
                  </div>
                ) : null}
              </div>
            ))}
          </React.Fragment>
        ) : null}

        {submission.video ? (
          <React.Fragment>
            <div className={classes.eyebrow}>{FEEDBACK_EYEBROW}</div>
            <div className={classes.videoLink} onClick={() => onPlay(submission)} role={'button'}>
              <span className={classes.playBox}>
                <span className={classes.playTriangle} />
              </span>
              {VIDEO_LINK_COPY}
            </div>
          </React.Fragment>
        ) : null}
      </div>
    </div>
  )
}

/**
 * The individual exit-survey submissions behind the charts above. All four controls filter the list that
 * was already loaded with the page — no refetch — which is how the rest of this page behaves.
 * @param {Object} props
 * @param {Array<import('../../lib/MetricData/metricDataUtilities').ExitSurveySubmission>} props.submissions
 */
function SubmissionDetails({submissions}) {
  const classes = useStyles()

  const [categoryId, setCategoryId] = useState(ALL_CATEGORIES)
  const [windowEnd, setWindowEnd] = useState(DATE_OPTIONS[0].value)
  const [npsScore, setNpsScore] = useState(NPS_SCORE_ALL)
  const [search, setSearch] = useState('')
  const [playingResponse, setPlayingResponse] = useState(null)

  const categoryOptions = useMemo(() => categoryOptionsFrom(submissions), [submissions])

  const filtered = useMemo(() => {
    const term = search.trim().toLowerCase()
    // the option value is the window's end; the window itself is the 30 days up to it, per its own label
    const rangeEnd = moment(windowEnd).endOf('day')
    const rangeStart = moment(windowEnd).subtract(DATE_WINDOW_DAYS, 'days').startOf('day')

    return submissions.filter(submission => {
      // cheapest test first, ahead of the reasonGroups scan
      if (npsScore !== NPS_SCORE_ALL && getNPSBucket(submission.npsRating) !== npsScore) {
        return false
      }
      if (categoryId !== ALL_CATEGORIES && !(submission.reasonGroups || []).some(group => group.id === categoryId)) {
        return false
      }
      const submittedAt = moment(submission.submittedAt)
      if (submittedAt.isBefore(rangeStart) || submittedAt.isAfter(rangeEnd)) {
        return false
      }

      return matchesSearch(submission, term)
    })
  }, [submissions, categoryId, windowEnd, npsScore, search])

  return (
    <div className={classes.section}>
      <div className={classes.title}>{TITLE}</div>

      {submissions.length === 0 ? (
        <DataComingSoon />
      ) : (
        <React.Fragment>
          <div className={classes.controls}>
            <div className={classes.control}>
              {/* SelectInput only draws its border when `label` is a string, hence the empty one */}
              <SelectInput label={''} options={categoryOptions} value={categoryId} onChange={setCategoryId} />
            </div>
            <div className={classes.control}>
              <SelectInput label={''} options={DATE_OPTIONS} value={windowEnd} onChange={setWindowEnd} />
            </div>
            <div className={classes.control}>
              <SelectInput label={''} options={npsScoreOptions} value={npsScore} onChange={setNpsScore} renderValue={renderNpsScoreValue} />
            </div>
            <div className={classes.control}>
              <ClearableInput value={search} onChange={setSearch} placeholder={'Search'} />
            </div>
          </div>

          <div className={classes.count}>{`${filtered.length} ${filtered.length === 1 ? 'response' : 'responses'}`}</div>

          <div className={classes.list}>
            {filtered.length === 0 ? (
              // the same zero-results treatment as Resident Sentiment's detail lists
              <EmptyStateIconAndMessage message={NO_MATCHES} icon={RXRIcon.ELLIPSIS} />
            ) : (
              filtered.map(submission => <SubmissionRow key={submission.id} submission={submission} onPlay={setPlayingResponse} />)
            )}
          </div>
        </React.Fragment>
      )}

      <ExitSurveyVideoPlayerModal response={playingResponse} onClose={() => setPlayingResponse(null)} />
    </div>
  )
}

const useStyles = makeStyles({
  section: {
    marginTop: Spacing.spaceMedium,
    marginBottom: Spacing.spaceMedium,
    padding: Spacing.spaceMedium,
    backgroundColor: Colors.rxrWhiteColor,
    border: `1px solid ${Colors.rxrGreyColor}`,
    borderRadius: borderRadiusMedium,
    boxShadow: Colors.shadowSoft,
  },
  title: {
    marginBottom: Spacing.spaceMedium,
    fontSize: Typography.fontSizeLarge,
    fontWeight: Typography.heavyFontWeight,
    color: Colors.rxrBlackColor,
  },
  controls: {
    display: 'flex',
    alignItems: 'center',
    gap: Spacing.spaceMedium,
  },
  control: {
    flex: 1,
    // without this a long selected option stretches its column instead of ellipsing inside it
    minWidth: 0,
  },
  count: {
    paddingTop: Spacing.spaceMedium,
    paddingBottom: Spacing.spaceExtraSmall,
    fontSize: Typography.fontSizeSmall,
    fontWeight: Typography.lightFontWeight,
    color: Colors.rxrDarkGreyColor,
    letterSpacing: 1,
    textTransform: 'uppercase',
  },
  list: {
    border: `1px solid ${Colors.rxrGreyColor}`,
    borderRadius: borderRadiusMedium,
    maxHeight: LIST_MAX_HEIGHT,
    overflowY: 'auto',
  },
  row: {
    display: 'flex',
    gap: Spacing.spaceMedium,
    padding: Spacing.spaceMedium,
    borderBottom: `1px solid ${Colors.rxrGreyColor}`,

    '&:last-child': {
      borderBottom: 'none',
    },
  },
  rating: {
    flexShrink: 0,
    display: 'flex',
    flexDirection: 'column',
    alignItems: 'center',
    justifyContent: 'center',
    width: RATING_SIZE,
    height: RATING_SIZE,
    borderRadius: '50%',
    backgroundColor: Colors.rxrTint25Color,
    color: Colors.rxrBlackColor,
  },
  ratingValue: {
    fontSize: Typography.fontSizeLarge,
    fontWeight: Typography.mediumFontWeight,
    lineHeight: 1,
  },
  ratingDenominator: {
    fontSize: Typography.fontSizeExtraSmall,
    fontWeight: Typography.lightFontWeight,
    color: Colors.rxrDarkGreyColor,
  },
  rowBody: {
    flex: 1,
    minWidth: 0,
  },
  rowHeader: {
    display: 'flex',
    alignItems: 'baseline',
    justifyContent: 'space-between',
    gap: Spacing.spaceMedium,
  },
  residentName: {
    fontSize: Typography.fontSizeLarge,
    fontWeight: Typography.mediumFontWeight,
    color: Colors.rxrBlackColor,
  },
  submittedAt: {
    flexShrink: 0,
    fontSize: Typography.fontSizeMedium,
    fontWeight: Typography.lightFontWeight,
    color: Colors.rxrDarkGreyColor,
  },
  eyebrow: {
    marginTop: Spacing.spaceSmall,
    fontSize: Typography.fontSizeSmall,
    fontWeight: Typography.lightFontWeight,
    color: Colors.rxrDarkGreyColor,
    letterSpacing: 1,
    textTransform: 'uppercase',
  },
  reasonGroup: {
    marginTop: Spacing.spaceExtraSmall,
  },
  macroChip: {
    display: 'inline-block',
    padding: `4px ${Spacing.spaceSmall}px`,
    borderRadius: borderRadiusMedium,
    border: `1px solid ${Colors.rxrPrimaryColor}`,
    backgroundColor: Colors.rxrTint15Color,
    color: Colors.rxrPrimaryColor,
    fontSize: Typography.fontSizeMedium,
    fontWeight: Typography.mediumFontWeight,
  },
  subReasons: {
    display: 'flex',
    flexWrap: 'wrap',
    gap: Spacing.spaceExtraSmall,
    // the indent plus the rule stand in for the design's elbow connector back up to the macro chip
    marginTop: Spacing.spaceExtraExtraSmall,
    marginLeft: Spacing.spaceMedium,
    paddingLeft: Spacing.spaceMedium,
    borderLeft: `1px solid ${Colors.rxrGreyColor}`,
  },
  subChip: {
    display: 'inline-block',
    padding: `4px ${Spacing.spaceSmall}px`,
    borderRadius: borderRadiusMedium,
    border: `1px solid ${Colors.rxrGreyColor}`,
    backgroundColor: Colors.nearWhiteColor,
    color: Colors.rxrDarkGreyColor,
    fontSize: Typography.fontSizeMedium,
    fontWeight: Typography.lightFontWeight,
  },
  videoLink: {
    display: 'inline-flex',
    alignItems: 'center',
    gap: Spacing.spaceExtraSmall,
    marginTop: Spacing.spaceExtraSmall,
    padding: `4px ${Spacing.spaceSmall}px`,
    borderRadius: borderRadiusMedium,
    backgroundColor: Colors.rxrTint15Color,
    color: Colors.rxrPrimaryColor,
    fontSize: Typography.fontSizeMedium,
    cursor: 'pointer',
  },
  // RXRIcon has no video glyph, so this is the same border triangle ExitSurveyVideoCard draws
  playBox: {
    display: 'inline-flex',
    alignItems: 'center',
    justifyContent: 'center',
    width: 22,
    height: 16,
    borderRadius: Spacing.spaceExtraExtraSmall,
    backgroundColor: Colors.rxrPrimaryColor,
  },
  playTriangle: {
    marginLeft: 2,
    borderTop: '4px solid transparent',
    borderBottom: '4px solid transparent',
    borderLeft: `6px solid ${Colors.rxrWhiteColor}`,
  },
})

const submissionShape = PropTypes.shape({
  id: PropTypes.string.isRequired,
  residentName: PropTypes.string.isRequired,
  unitNumber: PropTypes.string,
  submittedAt: PropTypes.string,
  npsRating: PropTypes.number,
  reasonGroups: PropTypes.arrayOf(
    PropTypes.shape({
      id: PropTypes.string.isRequired,
      label: PropTypes.string.isRequired,
      subReasons: PropTypes.array,
    }),
  ),
  video: PropTypes.shape({
    url: PropTypes.string.isRequired,
    thumbnailUrl: PropTypes.string,
    durationSeconds: PropTypes.number,
  }),
})

SubmissionRow.propTypes = {
  submission: submissionShape.isRequired,
  onPlay: PropTypes.func.isRequired,
}

SubmissionDetails.propTypes = {
  submissions: PropTypes.arrayOf(submissionShape).isRequired,
}

export default SubmissionDetails
